import usePermissions from '@/hooks/usePermissions';
import { useLazyGetTrancheByTrancheNameAndFundKeyQuery } from '@/services/funds';
import {
  useGetDirectPayMaterialVendorsQuery,
  useGetOrganizationDirectPaySettingsQuery,
  useGetPerformProgressQuery,
} from '@/services/organizations';
import { useGetPaymentMethodsByAccountIdQuery } from '@/services/paymentMethods';
import { usePostEstimatedPricingMutation } from '@/services/pricing';
import { useGetSalesRepProviderUserBySalesRepIdQuery } from '@/services/salesReps';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  AccountMilestoneStatus,
  AdderType,
  AttestmentTypeLabelMap,
  DirectPayProjectStatus,
  DomesticContentEligibilityTypes,
  ExternalReferenceType,
  MilestoneType,
  ProgramType,
  QuoteStatus,
  UserPermissions,
} from 'types';

import {
  useGetAccountDirectPayDetailsQuery,
  useGetAccountQuery,
  useGetDirectPayProgressQuery,
  useGetFinancingDataQuery,
} from '../../services/accounts';
import { useGetAddersQuery } from '../../services/adders';
import { useGetQuotesQuery } from '../../services/quotes';
import { useGetCurrentDesignQuery } from '../../services/systemDesign';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isPalmettoFinanceUserIncludingRoot } from '../auth/RequireIsInternalUser';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../auth/requirePermissions';
import { PropertyListBox } from '../PropertyListBox/PropertyListBox';

import { getSystemDesignLabel } from './Account';
import { AccountDetailsModals } from './components/AccountDetailsModals';
import { AccountDetailsContext, useAccountDetailsContext } from './hooks/AccountDetailsContext';
import { extractFundKeyUsingTrancheName } from './hooks/extractFundKeyUsingTrancheName';
import { getSystemDesignProperties } from './hooks/getSystemDesignProperties';
import { useAccountDetailsModalState } from './hooks/useAccountDetailsModals';
import { useAccountProperties } from './hooks/useAccountProperties';
import { useAddersProperties } from './hooks/useAddersProperties';
import { useAssetSaleProperties } from './hooks/useAssetSaleProperties';
import { useDirectPayProperties } from './hooks/useDirectPayProperties';
import { useITCAddersProperties } from './hooks/useITCAddersProperties';
import { usePartnerOrgProperties } from './hooks/usePartnerOrgProperties';
import { usePricingProperties } from './hooks/usePricingProperties';
import { useSalesRepProperties } from './hooks/useSalesRepProperties';
import { useSupportProperties } from './hooks/useSupportProperties';

const programTypesShouldVerifySalesRep = [
  ProgramType.solar,
  ProgramType.newHomes,
  ProgramType.energyBackup,
] as const;

export function AccountDetails() {
  const { claims } = useAuth();
  const userPermissions = usePermissions();
  const { id } = useParams<{ id: any }>();
  const { data: account, isLoading } = useGetAccountQuery(id);
  const { data: isAccountEditEnabled } = useGetPerformProgressQuery(id);

  const isLightReachAdmin = isPalmettoFinanceUser(claims) && userPermissions.includes(UserPermissions.admin);

  const { activeModal, openModal, closeModal } = useAccountDetailsModalState();

  const contextValue = useMemo(
    () => ({
      activeModal,
      openModal,
      closeModal,
      account,
      id,
      isLoading,
      claims,
      userPermissions,
      isLightReachAdmin,
      isAccountEditEnabled,
    }),
    [
      activeModal,
      openModal,
      closeModal,
      account,
      id,
      isLoading,
      claims,
      userPermissions,
      isLightReachAdmin,
      isAccountEditEnabled,
    ],
  );

  return (
    <AccountDetailsContext.Provider value={contextValue}>
      <AccountDetailsContent />
    </AccountDetailsContext.Provider>
  );
}

function AccountDetailsContent() {
  const { account, id, isLoading, claims, isAccountEditEnabled, activeModal, closeModal } =
    useAccountDetailsContext();
  const programType = account?.programType;

  const { data: directPaySettings } = useGetOrganizationDirectPaySettingsQuery(
    { alias: account?.organizationId, programType },
    { skip: !account?.organizationId },
  );
  const isNonEquipmentProgram =
    !!programType &&
    ![ProgramType.hvac, ProgramType.waterHeater, ProgramType.generator, ProgramType.energyBackup].includes(
      programType,
    );
  const { data: paymentMethod = [] } = useGetPaymentMethodsByAccountIdQuery({ id }, { skip: !id });
  const { data: salesRepProviderUser, isLoading: isSalesRepInfoLoading } =
    useGetSalesRepProviderUserBySalesRepIdQuery(
      { salesRepId: account?.salesRepVerification?.salesRepReferenceId },
      { skip: !account?.salesRepVerification?.salesRepReferenceId },
    );
  const { data: directPayDetails } = useGetAccountDirectPayDetailsQuery(id, { skip: !id });
  // While a Direct Pay opt-in/opt-out is processing in the worker, keep an SSE subscription open so
  // the badge flips to the final status (or rolls back) when the terminal event arrives. Mirrors how
  // ContractListItem drives useGetContractSendProgressQuery off the contract's sending status.
  useGetDirectPayProgressQuery(
    { accountId: id },
    { skip: !id || account?.directPay?.projectStatus !== DirectPayProjectStatus.processing },
  );
  const hasDirectPay = directPayDetails?.directPayEnabled || directPayDetails?.directPayMaterialVendorAlias;
  const { data: directPayMaterialVendors } = useGetDirectPayMaterialVendorsQuery(undefined, {
    skip: !hasDirectPay,
  });
  const hasConcordAccountBeenCreated = account?.externalReferenceIds?.find(
    (ref: any) => ref.type === ExternalReferenceType.concord,
  )?.id;
  const { data: financingData } = useGetFinancingDataQuery(id, {
    skip: !id || !isPalmettoFinanceUserIncludingRoot(claims) || !hasConcordAccountBeenCreated,
  });
  const { data: systemDesign } = useGetCurrentDesignQuery({ id, programType }, { skip: !programType });
  const { data: quotes } = useGetQuotesQuery({ id, programType }, { skip: !programType });
  const activeQuote = quotes?.find((quote) => quote.status === QuoteStatus.active);
  const solarReadinessCost = activeQuote?.additionalCostAdders?.find(
    (adder: any) => adder.type === AdderType.solarReadiness,
  )?.cost;
  const { data: addersData } = useGetAddersQuery(id, { skip: !isNonEquipmentProgram });
  const [postEstimatedPricing, { data: estimatedPricingData }] = usePostEstimatedPricingMutation({
    fixedCacheKey: `estimatedPricing-${id}-dcCheck`,
  });
  const allowsAction = useIsOrgReadOnly();
  const { salesRepVerification, adjustMonthlyPaymentCap, enrollInDomesticContentUi } = useFlags();

  const [trancheDetails, setTrancheDetails] = useState<Record<string, any>>({});
  const [getTrancheByTrancheNameAndFundKeyQuery] = useLazyGetTrancheByTrancheNameAndFundKeyQuery();
  const orgDirectPaySettings = directPaySettings?.data?.[0];

  const isMilestoneEligibleForDC =
    account?.currentMilestone?.type === MilestoneType.noticeToProceed ||
    (account?.currentMilestone?.type === MilestoneType.install &&
      account?.currentMilestone?.status === AccountMilestoneStatus.pending);

  useEffect(() => {
    if (
      isMilestoneEligibleForDC &&
      account?.priceSheetMappingId &&
      account?.utility?.lseId &&
      account?.address?.state &&
      programType &&
      account?.organizationId &&
      systemDesign?.systemSizeKw &&
      systemDesign?.systemFirstYearProductionKwh &&
      [ProgramType.solar, ProgramType.newHomes].includes(programType)
    ) {
      postEstimatedPricing({
        lseId: account.utility.lseId,
        state: account.address.state,
        systemSizeKw: systemDesign.systemSizeKw,
        systemFirstYearProductionKwh: systemDesign.systemFirstYearProductionKwh,
        includeDomesticContentPricing: true,
        organizationId: account.organizationId,
        programType,
      });
    }
  }, [
    isMilestoneEligibleForDC,
    account?.priceSheetMappingId,
    account?.utility?.lseId,
    account?.address?.state,
    programType,
    systemDesign?.systemSizeKw,
    systemDesign?.systemFirstYearProductionKwh,
    account?.organizationId,
    postEstimatedPricing,
  ]);

  useEffect(() => {
    if (account?.tranches) {
      const fetchTrancheDetails = async () => {
        const fetchedDetails: Record<string, any> = {};

        const trancheRequests = account.tranches.map(async (tranche: string) => {
          const fundKey = extractFundKeyUsingTrancheName(tranche);
          const { data } = await getTrancheByTrancheNameAndFundKeyQuery({
            fundKey,
            trancheName: tranche,
          });

          fetchedDetails[tranche] = data;
        });

        await Promise.all(trancheRequests);

        setTrancheDetails(fetchedDetails);
      };

      fetchTrancheDetails();
    }
  }, [account?.tranches, getTrancheByTrancheNameAndFundKeyQuery]);

  const shouldVerifySalesRep = salesRepVerification && programTypesShouldVerifySalesRep.includes(programType);

  const domesticContentEligibilityStatus = account?.domesticContentEligibilityStatus ?? '';
  const dcAttestmentType =
    systemDesign?.domesticContentAttestment?.attestmentType &&
    (domesticContentEligibilityStatus === DomesticContentEligibilityTypes.confirmed ||
      domesticContentEligibilityStatus === DomesticContentEligibilityTypes.underConsideration)
      ? ' - ' +
        AttestmentTypeLabelMap[
          systemDesign.domesticContentAttestment.attestmentType as keyof typeof AttestmentTypeLabelMap
        ]
      : '';

  const canEnrollInDomesticContent = useMemo(() => {
    if (!account) return false;

    const isProgramEligible = [ProgramType.solar, ProgramType.newHomes].includes(programType);

    const hasExistingDomesticContentEnrollment =
      Boolean(account.orgDomesticContentModifierId) ||
      account.itcAdderQualifications?.domesticContentEligible === true ||
      domesticContentEligibilityStatus === DomesticContentEligibilityTypes.confirmed ||
      domesticContentEligibilityStatus === DomesticContentEligibilityTypes.underConsideration;

    const isNotAlreadyEnrolled = !hasExistingDomesticContentEnrollment;
    const isMilestoneEligible =
      account.currentMilestone?.type === MilestoneType.noticeToProceed ||
      (account.currentMilestone?.type === MilestoneType.install &&
        account.currentMilestone?.status === AccountMilestoneStatus.pending);

    const hasPriceSheetMapping = Boolean(account.priceSheetMappingId);

    const hasDomesticContentPricingAvailable = estimatedPricingData?.some(
      (product: any) => product.domesticContentEnabled,
    );

    return (
      isProgramEligible &&
      isNotAlreadyEnrolled &&
      isMilestoneEligible &&
      hasPriceSheetMapping &&
      hasDomesticContentPricingAvailable
    );
  }, [account, estimatedPricingData, domesticContentEligibilityStatus]);

  const directPaySubCompanyName = useMemo(() => {
    if (
      !directPayDetails?.subCompanyId ||
      !directPayMaterialVendors?.materialVendors ||
      !directPayDetails?.directPayMaterialVendorAlias
    ) {
      return '';
    }

    const matchingVendor = directPayMaterialVendors.materialVendors.find(
      (vendor: any) => vendor.directPayMaterialVendorAlias === directPayDetails.directPayMaterialVendorAlias,
    );

    if (!matchingVendor) return '';

    const matchingSubCompany = matchingVendor.subCompanies?.find(
      (subCompany: any) => subCompany.id === directPayDetails.subCompanyId,
    );

    return matchingSubCompany?.name || '';
  }, [directPayDetails, directPayMaterialVendors]);

  const monthlyPaymentCapText = account?.monthlyPaymentCap
    ? account.monthlyPaymentCap.override
      ? 'Removed'
      : `$${account.monthlyPaymentCap.greaterThanZeroEscalationRate} >0% Esc, $${account.monthlyPaymentCap.zeroEscalationRate} 0% Esc`
    : '';

  // Property hooks - each receives only its unique data
  const accountDetailsProperties = useAccountProperties({ domesticContentEligibilityStatus, dcAttestmentType });
  const supportDetailsProperties = useSupportProperties({ paymentMethod, financingData });
  const { assetSaleProperties, allocationProperties } = useAssetSaleProperties({ trancheDetails });
  const addersProperties = useAddersProperties({ activeQuote, addersData, solarReadinessCost });
  const salesRepProperties = useSalesRepProperties({ shouldVerifySalesRep });
  const partnerOrgProperties = usePartnerOrgProperties();
  const directPayProperties = useDirectPayProperties({ directPayDetails, directPaySubCompanyName, allowsAction });
  const pricingDetailsBoxProperties = usePricingProperties({ monthlyPaymentCapText, adjustMonthlyPaymentCap });
  const itcAdderProperties = useITCAddersProperties({
    systemDesign,
    domesticContentEligibilityStatus,
    dcAttestmentType,
    canEnrollInDomesticContent: !!canEnrollInDomesticContent,
    enrollInDomesticContentUi,
  });

  const pvSystemProperties = {
    heading: getSystemDesignLabel(programType),
    editPath: isAccountEditEnabled ? `/accounts/${id}/system-design` : undefined,
    values: getSystemDesignProperties(account, systemDesign),
    shouldFold: true,
  };

  return (
    <>
      <AccountDetailsModals
        activeModal={activeModal}
        closeModal={closeModal}
        id={id}
        account={account}
        directPayDetails={directPayDetails}
        directPayMaterialVendorAliasOnAccount={account?.directPay?.directPayMaterialVendorAlias}
        orgDirectPaySettings={orgDirectPaySettings}
        paymentMethod={paymentMethod}
        currentMonthlyPaymentCap={account?.monthlyPaymentCap}
        financingData={financingData}
        hasConcordAccountBeenCreated={!!hasConcordAccountBeenCreated}
        shouldVerifySalesRep={shouldVerifySalesRep}
        isSalesRepInfoLoading={isSalesRepInfoLoading}
        salesRepProviderUser={salesRepProviderUser}
        hasActiveQuote={!!activeQuote}
      />
      {!isLoading && account && directPayDetails && (
        <>
          <PropertyListBox {...accountDetailsProperties} />
          <RequirePermissions permissions={[UserPermissions.lightReachSupport]}>
            <PropertyListBox {...supportDetailsProperties} />
          </RequirePermissions>
          <PropertyListBox {...pvSystemProperties} />
          {![ProgramType.hvac, ProgramType.waterHeater, ProgramType.generator, ProgramType.energyBackup].includes(
            programType,
          ) && <PropertyListBox {...addersProperties} />}
          <PropertyListBox {...salesRepProperties} />
          <PropertyListBox {...partnerOrgProperties} />
          {pricingDetailsBoxProperties && <PropertyListBox {...pricingDetailsBoxProperties} />}
          {(directPayDetails?.directPayEnabled ||
            directPayDetails?.projectStatus === DirectPayProjectStatus.optOut ||
            orgDirectPaySettings?.directPayDefaultEnabled ||
            (orgDirectPaySettings?.stateOverrides && orgDirectPaySettings.stateOverrides.length > 0) ||
            (orgDirectPaySettings?.additionalOptInVendors &&
              orgDirectPaySettings.additionalOptInVendors.length > 0)) && (
            <PropertyListBox {...directPayProperties} />
          )}
          {isPalmettoFinanceUser(claims) &&
            [ProgramType.newCommunity, ProgramType.newHomes, ProgramType.solar, ProgramType.energyBackup].includes(
              programType,
            ) && <PropertyListBox {...itcAdderProperties} />}
          <RequirePermissions
            permissions={[UserPermissions.lightReachPaymentAdmin, UserPermissions.lightReachSupport]}
            checkAllPermissions={false}
          >
            <PropertyListBox {...assetSaleProperties} />
            <PropertyListBox {...allocationProperties} />
          </RequirePermissions>
        </>
      )}
    </>
  );
}
